/**
 * Labeled select built on Radix, with empty-value sentinel support.
 */

import * as React from 'react';
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info, Loader2 } from 'lucide-react';

/** Radix {@link SelectItem} cannot use empty string; map option.value === '' to this sentinel. */
const EMPTY_OPTION_VALUE = '__select_empty__';

/** Scroll distance from bottom to trigger load-more. */
const SCROLL_LOAD_THRESHOLD_PX = 40;

/** Map empty option value to the Radix sentinel. */
const toSelectItemValue = (value: string): string => (value === '' ? EMPTY_OPTION_VALUE : value);

/** `aria-describedby` target for error or helper text. */
function describedById(id: string, error?: string, helperText?: string): string | undefined {
  if (error) return `${id}-error`;
  if (helperText) return `${id}-helper`;
  return undefined;
}

/** Bound value for Radix; empty string is only valid when an empty option exists. */
function boundSelectValue(value: string | undefined, hasEmptyOption: boolean) {
  if (value !== '') return value;
  return hasEmptyOption ? EMPTY_OPTION_VALUE : undefined;
}

/** Map the Radix sentinel back to an empty string. */
const fromSelectItemValue = (value: string): string => (value === EMPTY_OPTION_VALUE ? '' : value);

/** One option in {@link Select}. */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Mounted so the trigger can show a stored value, but not listed in the menu. */
  hidden?: boolean;
  icon?: React.ReactNode;
  color?: string;
  tooltip?: string;
}

/** Label row above the select, including an optional info tooltip. */
function SelectLabel({
  label,
  labelTooltip,
  selectId,
  required,
}: Readonly<{
  label?: string;
  labelTooltip?: string;
  selectId: string;
  required?: boolean;
}>): React.ReactNode {
  if (!label) return null;
  return (
    <Label
      htmlFor={selectId}
      className={cn('form-label inline-flex items-center', required && 'form-label-required')}
    >
      {label}
      {labelTooltip ? <SelectLabelTooltip text={labelTooltip} /> : null}
    </Label>
  );
}

/** Info icon that shows `text` on hover. */
function SelectLabelTooltip({ text }: Readonly<{ text: string }>): React.ReactElement {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="ml-1 inline-flex shrink-0 translate-y-px cursor-help bg-transparent text-[var(--color-2-nuance-1)]">
            <Info size={13} className="block" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Props for {@link Select}. */
interface SelectProps {
  label?: string;
  /** Info tooltip shown beside the label. */
  labelTooltip?: string;
  placeholder?: string;
  options: readonly SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  state?: 'default' | 'error' | 'success' | 'warning';
  side?: 'top' | 'bottom';
  contentClassName?: string;
}

/** Labeled single-select built on the Radix Select primitive. */
export const Select: React.FC<SelectProps> = ({
  label,
  labelTooltip,
  placeholder = 'Sélectionner une option',
  options,
  value,
  onChange,
  onBlur,
  onLoadMore,
  hasMore,
  isLoading = false,
  error,
  helperText,
  required,
  disabled,
  className,
  size = 'md',
  state,
  side,
  contentClassName,
}) => {
  const selectId = `select-${label?.toLowerCase().replaceAll(/\s+/g, '-')}`;
  const [open, setOpen] = React.useState(false);

  const effectiveState = error ? 'error' : state || 'default';
  /** Empty option is allowed only when an option with `value === ''` exists. */
  const hasEmptyOption = options.some((option) => option.value === '');
  /** Drop blank empty-value rows that have no label. */
  const selectableOptions = options.filter(
    (option) => option.value !== '' || option.label.trim().length > 0,
  );

  const currentOption = selectableOptions.find(
    (o) => toSelectItemValue(o.value) === boundSelectValue(value, hasEmptyOption),
  );

  /** Open on the next frame so the trigger click does not immediately close. */
  const openSelect = () => {
    requestAnimationFrame(() => {
      setOpen(true);
    });
  };
  const closeSelect = () => {
    setOpen(false);
  };

  /** Trigger load-more when the user scrolls near the bottom of the dropdown. */
  React.useEffect(() => {
    if (!open || !onLoadMore || !hasMore) return;

    // Find the Radix viewport element after the portal renders
    const checkViewport = () => {
      const viewport = document.querySelector('[role="listbox"]');
      if (!viewport) return;

      const handleScroll = () => {
        const distanceFromBottom =
          viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
        if (distanceFromBottom < SCROLL_LOAD_THRESHOLD_PX) {
          onLoadMore();
        }
      };

      viewport.addEventListener('scroll', handleScroll, { passive: true });
      return () => viewport.removeEventListener('scroll', handleScroll);
    };

    // Wait for portal to mount
    const timer = setTimeout(checkViewport, 0);
    return () => clearTimeout(timer);
  }, [open, onLoadMore, hasMore]);

  return (
    <div className={cn('w-full space-y-2', !label && 'min-w-[200px]', className)}>
      <SelectLabel
        label={label}
        labelTooltip={labelTooltip}
        selectId={selectId}
        required={required}
      />
      <ShadcnSelect
        value={boundSelectValue(value, hasEmptyOption)}
        onValueChange={(nextValue) => onChange?.(fromSelectItemValue(nextValue))}
        disabled={disabled}
        open={open}
        onOpenChange={(nextOpen) => {
          if (nextOpen) openSelect();
          else closeSelect();
        }}
      >
        <SelectTrigger
          id={selectId}
          onBlur={onBlur}
          className={cn(
            effectiveState === 'error' && 'modern-input-error',
            effectiveState === 'success' && 'modern-input-success',
            effectiveState === 'warning' && 'modern-input-warning',
            size === 'sm' && 'h-9 px-3 py-2 text-sm',
            size === 'lg' && 'h-13 px-5 py-3.5 text-lg',
            'w-full',
          )}
          aria-invalid={effectiveState === 'error'}
          aria-describedby={describedById(selectId, error, helperText)}
        >
          {currentOption?.icon ? (
            <div className="flex items-center gap-1.5 w-full overflow-hidden">
              <span
                className="shrink-0 flex items-center"
                style={currentOption.color ? { color: currentOption.color } : undefined}
              >
                {currentOption.icon}
              </span>
              <span className="truncate flex-1 text-left">{currentOption.label}</span>
            </div>
          ) : (
            <SelectValue placeholder={placeholder} />
          )}
        </SelectTrigger>
        <SelectContent
          side={side}
          className={cn(size === 'lg' ? 'modern-dropdown-lg' : undefined, contentClassName)}
        >
          {selectableOptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[color-mix(in_srgb,var(--color-1-nuance-3)_20%,transparent)] flex items-center justify-center mb-3">
                <svg
                  className="w-6 h-6 text-muted-foreground"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Aucune option disponible
              </p>
              <p className="text-xs text-muted-foreground/70">
                {label ? `Aucun(e) ${label.toLowerCase()} à sélectionner` : 'Liste vide'}
              </p>
            </div>
          ) : (
            selectableOptions.map((option) => {
              const itemContent = option.icon ? (
                <span className="flex items-center gap-1.5">
                  <span
                    className="shrink-0"
                    style={option.color ? { color: option.color } : undefined}
                  >
                    {option.icon}
                  </span>
                  {option.label}
                </span>
              ) : (
                option.label
              );

              const selectItem = (
                <SelectItem
                  key={option.tooltip ? undefined : toSelectItemValue(option.value)}
                  value={toSelectItemValue(option.value)}
                  disabled={option.disabled}
                  hidden={option.hidden || undefined}
                  className={option.hidden ? 'hidden' : undefined}
                >
                  {itemContent}
                </SelectItem>
              );

              if (option.tooltip) {
                return (
                  <TooltipProvider key={toSelectItemValue(option.value)} delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>{selectItem}</TooltipTrigger>
                      <TooltipContent side="right">{option.tooltip}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }

              return selectItem;
            })
          )}
          {isLoading && selectableOptions.length > 0 && (
            <div className="flex items-center justify-center py-2 px-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </SelectContent>
      </ShadcnSelect>
      {error && (
        <p id={`${selectId}-error`} className="form-error-text" role="alert">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p id={`${selectId}-helper`} className="form-helper-text">
          {helperText}
        </p>
      )}
    </div>
  );
};
